/**
 * A CanvasItem's effective `texture_filter` and `texture_repeat`: PARENT_NODE takes the nearest concrete
 * ancestor value (`canvas_item.cpp:1625-1699`), else the viewport default: the project setting at the
 * root (`editor_node.cpp:451-460`) and the SubViewport's own property inside one.
 */
import { createContext, useContext, useMemo } from 'react';
import * as THREE from 'three';
import { CanvasItemTextureFilter, CanvasItemTextureRepeat, canvasItemSamplerFilter, canvasItemSamplerRepeat, } from '../godot/scene/canvasItem';
import { projectCanvasTextureDefaults } from '../parser/projectSettingsParser';
import { godotTextureFilterState } from '../resources/textures/godotTextureFilter';
import { useProjectSettings } from './contexts/ProjectSettingsContext';
/** three's wrapping for each of Godot's repeat modes. */
export const WRAP = {
    clamp: THREE.ClampToEdgeWrapping,
    repeat: THREE.RepeatWrapping,
    mirror: THREE.MirroredRepeatWrapping,
};
/**
 * The sampler state of a CanvasItem filter. The mipmap filters read the chain the texture has, and a
 * texture imported without one samples its base level under them, as the NEAREST and LINEAR samplers
 * do (`max_lod = 0`, `material_storage.cpp:2467-2476`). An anisotropic filter reads 4 taps whatever the
 * project or viewport level: the canvas binds the default samplers (`renderer_canvas_render_rd.cpp:460`),
 * which are allocated with the 4x default argument (`material_storage.cpp:1291`, `material_storage.h:370`).
 */
export function canvasItemFilterState(filter) {
    return godotTextureFilterState(canvasItemSamplerFilter(filter));
}
function canvasItemWrap(repeat) {
    return WRAP[canvasItemSamplerRepeat(repeat)];
}
/**
 * What a 2D view samples with `sampler`. NoColorSpace: with `hdr_2d` off (`rendering_server.cpp:3771`),
 * the canvas filters raw sRGB bytes (`texture_storage.cpp:754`), and `ControlQuad` decodes the sample.
 */
export function canvasItemViewSampler(sampler) {
    return {
        colorSpace: THREE.NoColorSpace,
        filter: canvasItemFilterState(sampler.filter),
        wrap: canvasItemWrap(sampler.repeat),
    };
}
/** No ancestor names either property, so the viewport default applies. */
export const ROOT_TEXTURE_SAMPLER = { filter: undefined, repeat: undefined };
export const TextureSampler2DContext = createContext(ROOT_TEXTURE_SAMPLER);
/**
 * Written only by a SubViewport: the default of its own canvas. `null`, with no SubViewport above,
 * is the root viewport, whose default is the project setting.
 */
const ViewportSamplerContext = createContext(null);
export const ViewportSamplerProvider = ViewportSamplerContext.Provider;
/** The ambient sampler, which an unnamed (PARENT_NODE) property resolves to. */
function useParentTextureSampler() {
    return useContext(TextureSampler2DContext);
}
/** What a PARENT_NODE item with nothing to defer to resolves to: its viewport's default. */
function useViewportTextureDefaults() {
    const viewport = useContext(ViewportSamplerContext);
    const { settings } = useProjectSettings();
    return useMemo(() => viewport ?? projectCanvasTextureDefaults(settings), [viewport, settings]);
}
/**
 * One property's resolution rule, shared by filter and repeat: an unnamed own value or the
 * property's PARENT_NODE defers to the ambient; a concrete one wins and is what gets published back out.
 */
function resolveInheritedSamplerValue(own, ambient, parentNode) {
    return own === undefined || own === parentNode ? ambient : own;
}
/** `own` laid over `ambient`: the sampler a node's descendants inherit, still with gaps where none names one. */
export function inheritTextureSampler(own, ambient) {
    return {
        filter: resolveInheritedSamplerValue(own.filter, ambient.filter, CanvasItemTextureFilter.PARENT_NODE),
        repeat: resolveInheritedSamplerValue(own.repeat, ambient.repeat, CanvasItemTextureRepeat.PARENT_NODE),
    };
}
/**
 * This node's effective filter and repeat, which its descendants inherit. Idempotent, unlike the
 * modulate product, so a painter may call it on its own properties. `skippedAncestors` names the
 * Node2D ancestors a promoted Control was walked past.
 */
export function useInheritedTextureSampler(ownFilter, ownRepeat, skippedAncestors = ROOT_TEXTURE_SAMPLER) {
    const parent = useParentTextureSampler();
    const viewport = useViewportTextureDefaults();
    const named = inheritTextureSampler({ filter: ownFilter, repeat: ownRepeat }, inheritTextureSampler(skippedAncestors, parent));
    const filter = named.filter ?? viewport.filter;
    const repeat = named.repeat ?? viewport.repeat;
    // Keyed on the two values, not the contexts: a new settings object with the same defaults must
    // not hand every textured item a new sampler, which re-clones and re-uploads its texture.
    return useMemo(() => ({ filter, repeat }), [filter, repeat]);
}
/** `useInheritedTextureSampler` for a Node2D's own properties. */
export function useNode2DTextureSampler(props) {
    return useInheritedTextureSampler(props.texture_filter, props.texture_repeat);
}
/**
 * The sampler a Control painter draws with. `ControlCanvasWalker` publishes the node's own effective
 * sampler around the painter, so the painter reads it as is, where a Node2D reads it before its own
 * group exists and so lays its properties over the ambient with `useInheritedTextureSampler`.
 */
export function useResolvedTextureSampler() {
    return useInheritedTextureSampler(undefined, undefined);
}
/**
 * The sampler an item draws a CanvasTexture with: the texture's own filter and repeat, each unless
 * it inherits (`texture_storage.cpp:737-738`), else the item's. `item` itself while nothing
 * overrides it, so a texture view keyed on the sampler keeps.
 */
export function useCanvasTextureSampler(item, texture) {
    const filter = texture?.textureFilter ?? CanvasItemTextureFilter.PARENT_NODE;
    const repeat = texture?.textureRepeat ?? CanvasItemTextureRepeat.PARENT_NODE;
    return useMemo(() => {
        const named = inheritTextureSampler({ filter, repeat }, item);
        const unchanged = named.filter === item.filter && named.repeat === item.repeat;
        return unchanged ? item : { filter: named.filter ?? item.filter, repeat: named.repeat ?? item.repeat };
    }, [item, filter, repeat]);
}
//# sourceMappingURL=canvasItemTextureSampler.js.map